import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Loader2, AlertCircle, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { useSortableData } from '../hooks/useSortableData';

// Column types for funnel details sorting
const FUNNEL_COLUMN_TYPES = {
  index: 'number',
  label: 'text',
  eventName: 'text',
  users: 'number'
};

// Sortable header component for funnel table
const FunnelSortableHeader = ({ column, label, sortColumn, sortDirection, onSort, align = 'left' }) => {
  const isActive = sortColumn === column;
  const ariaSort = isActive 
    ? (sortDirection === 'asc' ? 'ascending' : 'descending')
    : 'none';
  const alignClass = align === 'right' ? 'justify-end' : 'justify-start';

  return (
    <th className="py-2 px-3 text-xs font-medium text-muted-foreground" aria-sort={ariaSort}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${alignClass}`}
      >
        <span>{label}</span>
        <span className="flex-shrink-0 ml-0.5">
          {isActive ? (
            sortDirection === 'asc' ? (
              <ArrowUp className="h-3 w-3" />
            ) : (
              <ArrowDown className="h-3 w-3" />
            )
          ) : (
            <ArrowUpDown className="h-3 w-3 opacity-40" />
          )}
        </span>
      </button>
    </th>
  );
};

// Funnel Details Table with sorting
const FunnelDetailsTable = ({ steps, totalUsers }) => {
  const { sortedData, sortColumn, sortDirection, handleSort } = useSortableData(
    steps,
    { columnTypes: FUNNEL_COLUMN_TYPES }
  );

  return (
    <div className="border-t pt-4">
      <h4 className="text-sm font-semibold mb-3">Detalhes dos eventos</h4>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <FunnelSortableHeader
                column="index"
                label="#"
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
                align="left"
              />
              <FunnelSortableHeader
                column="label"
                label="Etapa"
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
                align="left"
              />
              <FunnelSortableHeader
                column="eventName"
                label="Evento"
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
                align="left"
              />
              <FunnelSortableHeader
                column="users"
                label="Usuários"
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
                align="right"
              />
            </tr>
          </thead>
          <tbody>
            {sortedData.map((step, idx) => {
              const cumulativeConversionRate = (step.users / totalUsers) * 100;
              
              return (
                <tr key={idx} className="border-b last:border-0 hover:bg-muted/50">
                  <td className="py-2 px-3">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-muted text-xs font-medium">
                      {step.index}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <p className="text-sm font-medium">{step.label}</p>
                  </td>
                  <td className="py-2 px-3">
                    <p className="text-xs text-muted-foreground">{step.eventName}</p>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <p className="text-sm font-medium">{step.users.toLocaleString()} usuários</p>
                    <p className="text-xs text-muted-foreground">
                      {cumulativeConversionRate.toFixed(1)}% do total
                    </p>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const FunnelChart = ({ 
  title, 
  data, 
  loading, 
  error, 
  colorScheme = 'purple' // 'purple' or 'green'
}) => {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
          <span className="text-muted-foreground">Carregando funil...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-900 mb-1">
                Não foi possível carregar este funil com dados reais do GA4.
              </p>
              <p className="text-xs text-red-800">
                Ative a opção 'Usar dados simulados (mock)' em Configurações do sistema para visualizar um exemplo de funil.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.steps || data.steps.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground py-6">
            Não há dados suficientes para este funil.
          </p>
        </CardContent>
      </Card>
    );
  }

  const barColor = colorScheme === 'purple' 
    ? 'bg-primary' 
    : 'bg-green-600';
  
  const conversionPillColor = colorScheme === 'purple'
    ? 'bg-purple-100 text-purple-700'
    : 'bg-green-100 text-green-700';

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>
          {data.totalUsers?.toLocaleString()} usuários no início
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Visual funnel bars */}
        <div className="space-y-4">
          {data.steps.map((step, idx) => {
            const barWidth = (step.users / data.totalUsers) * 100;
            
            return (
              <div key={idx} className="space-y-2">
                {/* Bar */}
                <div className="relative">
                  <div className={`${barColor} rounded-r-lg py-3 px-4 transition-all`}
                    style={{ width: `${barWidth}%`, minWidth: '30%' }}
                  >
                    <div className="flex justify-between items-center text-white">
                      <span className="font-medium text-sm">{step.label}</span>
                      <span className="font-bold">{step.users.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Pills */}
                {idx > 0 && (
                  <div className="flex gap-2 ml-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${conversionPillColor}`}>
                      Taxa de conversão: {(step.stepConversionRate * 100).toFixed(1)}%
                    </span>
                    <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">
                      Saída: {step.dropOffUsers.toLocaleString()} usuários ({(step.dropOffRate * 100).toFixed(1)}%)
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Steps detail list with sortable table */}
        <FunnelDetailsTable steps={data.steps} totalUsers={data.totalUsers} />
      </CardContent>
    </Card>
  );
};

export default FunnelChart;
